import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Truck, ArrowUpRight } from "lucide-react";

const TRUCK_IMG =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?q=80&w=1600&auto=format&fit=crop";

const SPECS = [
  { label: "Vehicle Class", value: "26-Foot Commercial Box Truck", testid: "spec-row-vehicle" },
  { label: "Lift Gate", value: "3,000 lb Heavy-Duty Hydraulic Gate", testid: "spec-row-liftgate" },
  { label: "Payload", value: "Up to 10,000 lbs — 12 Standard Pallets", testid: "spec-row-payload" },
  { label: "Security", value: "E-Track Tie-Down & Weather Guard", testid: "spec-row-security" },
  { label: "Onboard", value: "Pallet Jack Included on Every Run", testid: "spec-row-onboard" },
];

export const Equipment = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="equipment" data-testid="equipment-section" ref={ref} className="relative py-28 sm:py-36 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(circle at 30% 40%, rgba(200,90,50,0.14) 0%, transparent 60%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="absolute -inset-8 pointer-events-none" style={{ background: "radial-gradient(circle at center, rgba(232,157,75,0.16) 0%, transparent 70%)" }} />
          <div className="relative rounded-2xl overflow-hidden border border-[#C85A32]/35 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
            <motion.img
              src={TRUCK_IMG}
              alt="26-foot commercial box truck — the Desert Valley Freight workhorse"
              data-testid="equipment-image"
              style={{ y: imgY, scale: 1.18 }}
              className="w-full aspect-[4/3] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E1511]/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#F7F2EB] bg-[#1E1511]/70 backdrop-blur-md border border-[#C85A32]/40 rounded-full px-4 py-2">
              <Truck size={13} className="text-[#E89D4B]" />
              Unit 01 — The Workhorse
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C85A32] mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C85A32]" />
            04 // Equipment Spotlight
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F7F2EB] leading-tight mb-6">
            One truck. <span className="italic text-[#E89D4B]">Zero excuses.</span>
          </h2>
          <p className="text-base sm:text-lg text-[#E6DACB]/85 leading-relaxed mb-10 max-w-lg">
            A boutique fleet means your freight is never one of forty stops. It rides in a
            dedicated 26-foot box truck with the lift power to land heavy cargo anywhere.
          </p>

          <dl>
            {SPECS.map((spec) => (
              <div
                key={spec.label}
                data-testid={spec.testid}
                className="grid grid-cols-[130px_1fr] gap-4 items-baseline border-t border-[#C85A32]/20 py-4 last:border-b"
              >
                <dt className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#E89D4B]">{spec.label}</dt>
                <dd className="text-sm sm:text-base text-[#F7F2EB]">{spec.value}</dd>
              </div>
            ))}
          </dl>

          <a
            href="#quote"
            data-testid="equipment-quote-cta"
            className="group mt-10 inline-flex items-center gap-2 bg-[#C85A32] text-[#F7F2EB] font-mono text-xs tracking-[0.18em] uppercase rounded-full px-8 py-4 hover:bg-[#E89D4B] hover:text-[#1E1511] transition-colors duration-300"
          >
            Book This Truck
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
